<template>
  <div class="twig-picker-wrapper">
    <TButton
      v-if="$slots.buttonIcon"
      class="w-8 h-8"
      @click="openPicker"
      :disabled="disabledInput"
      :inverse="inverse"
    >
      <template #icon>
        <slot name="buttonIcon" />
      </template>
    </TButton>
    <t-input-color
      v-else
      ref="input"
      v-model="color"
      :with-input="withInput"
      :label="label"
      :disabled="disabledInput"
      :no-input="noInput"
      :no-border-right="noBorderRight"
      :short="short"
      :flex="flex"
      :medium="medium"
      :max-width="maxWidth"
      :changed="changed"
      :reset-tooltip="resetTooltip"
      @open-picker="openPicker"
      @reset="$emit('reset')"
    >
      <template v-if="$slots.label" #label>
        <slot name="label"></slot>
      </template>
    </t-input-color>

    <div
      id="color-picker"
      class="twig-picker"
      :class="{
        'no-input': noInput,
        'position-left': position === 'left',
        'position-right': position === 'right',
        'position-top': colorPickerTop,
      }"
      @keyup.esc="closePicker"
    >
      <color-picker
        v-if="pickerDisplayed"
        ref="outsideColorPicker"
        :theme="light ? 'light' : 'dark'"
        :color="color"
        :template-label="templateColorsLabel"
        :predefined-label="predefinedColorsLabel"
        :colors-default="presetColors"
        :template-colors="templateColors"
        :inverse="inverse"
        @changeColor="
          (c: string) => {
            color = c;
          }
        "
        @startSelectingColor="$emit('start-selecting-color')"
        @stopSelectingColor="$emit('stop-selecting-color')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import TButton from "../TButton/TButton.vue";
import TInputColor from "../TInputColor/TInputColor.vue";
import colorPicker from "./vue-colorpicker/Index.vue";
import { onClickOutside } from "@vueuse/core";
import { ref, computed, onMounted, PropType, useTemplateRef } from "vue";

type Color =
  | string
  | {
      rgba: {
        a: number;
      };
      hex: string;
    };

const props = withDefaults(
  defineProps<{
    defaultColor?: string;
    label?: string;
    templateColorsLabel?: string;
    predefinedColorsLabel?: string;
    withInput?: boolean;
    position?: string;
    colorPickerTop?: boolean;
    noBorderRight?: boolean;
    short?: boolean;
    medium?: boolean;
    noInput?: boolean;
    disabled?: boolean;
    flex?: boolean;
    light?: boolean;
    colors?: string[];
    templateColors?: string[];
    changed?: boolean;
    resetTooltip?: string;
    inverse?: boolean;
    maxWidth?: number;
  }>(),
  {
    templateColorsLabel: "Used colors",
    predefinedColorsLabel: "Predefined colors",
    withInput: true,
    position: "left",
    colors: () => [],
    templateColors: () => [],
    resetTooltip: "Clear value",
  },
);

const emits = defineEmits([
  "color-change",
  "get-template-colors",
  "start-selecting-color",
  "stop-selecting-color",
  "reset",
]);

const pickerDisplayed = ref(false);
const presetColors = ref([
  "rgba(0,0,0,0)",
  "#D0021B",
  "#F5A623",
  "#F8E71C",
  "#8B572A",
  "#7ED321",
  "#417505",
  "#BD10E0",
  "#9013FE",
  "#4A90E2",
  "#50E3C2",
  "#B8E986",
  "#000000",
  "#4A4A4A",
  "#9B9B9B",
  "#FFFFFF",
]);
const outsideColorPicker = useTemplateRef("outsideColorPicker");

const color = computed<string>({
  get() {
    //if attr is not present, try to use default color
    if (props.defaultColor) {
      return props.defaultColor;
    }

    //default color
    return "transparent";
  },

  set(val: Color) {
    //color was changed to the same color
    if (val === color.value) {
      return;
    }

    if (typeof val !== "string" && val.rgba && val.rgba.a === 0) {
      val = "transparent";
      emits("color-change", val);

      return;
    }

    // From Color Picker
    if (typeof val !== "string" && val.hex !== undefined) {
      emits("color-change", val.hex);
    }

    //From Input
    else {
      //set empty color to be transparent
      if (val === "") {
        val = "transparent";
      }

      emits("color-change", val);
    }
  },
});

const disabledInput = computed(() => {
  return props.disabled || pickerDisplayed.value;
});

function openPicker() {
  if (props.disabled) {
    return;
  }
  emits("get-template-colors");
  pickerDisplayed.value = true;
}

onClickOutside(outsideColorPicker, (e: any) => {
  closePicker();
});

function closePicker() {
  pickerDisplayed.value = false;
}

onMounted(() => {
  if (props.colors && props.colors.length > 0) {
    presetColors.value = [];
    presetColors.value.push(...props.colors);
  }
});
</script>

<style lang="scss" scoped>
.twig-picker {
  position: absolute;
  z-index: 9999999999;
  top: 36px;

  &.no-input {
    top: 43px;
  }
}

.twig-picker-wrapper {
  position: relative;
}

.position-left {
  left: 0;
}

.position-right {
  right: 0;
}

.position-top {
  top: -380px;
}
</style>
